import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  Database,
  Check,
  ChevronDown,
  ChevronRight,
  Info,
  Search,
} from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

import {
  getCollectionMeta,
  groupCollectionsByCategory,
} from './backupCollectionMeta';

const RestoreBackupModal = ({ backup, onClose, onConfirm }) => {
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const allCollections = backup?.collections || [];

  // Filter collections based on search
  const filteredCollections = useMemo(() => {
    if (!searchQuery.trim()) return allCollections;
    const query = searchQuery.toLowerCase();
    return allCollections.filter((c) => {
      const meta = getCollectionMeta(c.name);
      return (
        c.name.toLowerCase().includes(query) ||
        meta.description.toLowerCase().includes(query) ||
        meta.category.toLowerCase().includes(query)
      );
    });
  }, [allCollections, searchQuery]);

  const groupedCollections = groupCollectionsByCategory(filteredCollections);

  // Check if all filtered collections are selected
  const allSelected = filteredCollections.length > 0 &&
    filteredCollections.every((c) => selectedCollections.includes(c.name));

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Expand all categories
  const expandAll = () => {
    setExpandedCategories(new Set(groupedCollections.map((g) => g.category)));
  };

  // Handle select all filtered
  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelected = new Set(selectedCollections);
      filteredCollections.forEach((c) => newSelected.add(c.name));
      setSelectedCollections(Array.from(newSelected));
    } else {
      const filteredNames = new Set(filteredCollections.map((c) => c.name));
      setSelectedCollections(selectedCollections.filter((name) => !filteredNames.has(name)));
    }
  };

  // Handle category select all
  const handleSelectCategory = (category, collections, checked) => {
    const categoryNames = collections.map((c) => c.name);
    if (checked) {
      const newSelected = new Set(selectedCollections);
      categoryNames.forEach((name) => newSelected.add(name));
      setSelectedCollections(Array.from(newSelected));
    } else {
      setSelectedCollections(selectedCollections.filter((name) => !categoryNames.includes(name)));
    }
  };

  // Handle individual collection toggle
  const handleToggleCollection = (collectionName) => {
    setSelectedCollections((prev) =>
      prev.includes(collectionName)
        ? prev.filter((name) => name !== collectionName)
        : [...prev, collectionName]
    );
  };

  // Handle confirm
  const handleConfirm = () => {
    if (confirmText !== 'RESTORE') return;
    onConfirm(backup.backup_id, selectedCollections);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format bytes
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const canConfirm = confirmText === 'RESTORE' && selectedCollections.length > 0;

  // Get category selection state
  const getCategorySelectionState = (collections) => {
    const names = collections.map((c) => c.name);
    const selectedCount = names.filter((name) => selectedCollections.includes(name)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === names.length) return 'all';
    return 'partial';
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Database className="h-5 w-5" />
            Restore from Backup
          </DialogTitle>
          <DialogDescription className="space-y-2 pt-2">
            <p>
              Select which collections to restore. This will{' '}
              <strong>replace</strong> the current data in the selected collections.
            </p>
            <p>
              <strong>A pre-restore backup will be created automatically</strong> before
              restoration begins, so you can roll back if needed.
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Backup Info */}
          <div className="space-y-3 p-4 bg-stone-50 dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700">
            <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 dark:text-gray-100">Backup Details</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-stone-600 dark:text-stone-400">Created:</span>{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">{formatTimestamp(backup.started_at)}</span>
              </div>
              <div>
                <span className="text-stone-600 dark:text-stone-400">Size:</span>{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">
                  {backup.total_size_bytes ? formatBytes(backup.total_size_bytes) : '-'}
                </span>
              </div>
              <div>
                <span className="text-stone-600 dark:text-stone-400">Collections:</span>{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">{allCollections.length}</span>
              </div>
              <div>
                <span className="text-stone-600 dark:text-stone-400">Trigger:</span>{' '}
                <Badge variant="outline" className="ml-1">
                  {backup.trigger}
                </Badge>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">Important Warning</p>
              <p className="text-amber-800 dark:text-amber-300">
                Restoring will <strong>permanently replace</strong> the current data in the
                selected collections. Review the descriptions below to understand what each
                collection contains.
              </p>
            </div>
          </div>

          {/* Collection Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 dark:text-gray-100">
                Select Collections to Restore
              </h4>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  Expand All
                </Button>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                  />
                  <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                    Select All ({filteredCollections.length})
                  </Label>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search collections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Grouped Collections */}
            <div className="border border-stone-200 dark:border-stone-700 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
              {groupedCollections.length === 0 ? (
                <div className="p-8 text-center text-stone-500 dark:text-stone-400">
                  <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No collections match your search</p>
                </div>
              ) : (
                groupedCollections.map((group) => {
                  const isExpanded = expandedCategories.has(group.category);
                  const selectionState = getCategorySelectionState(group.collections);
                  const categoryDocs = group.collections.reduce((sum, c) => sum + (c.count || 0), 0);

                  return (
                    <Collapsible
                      key={group.category}
                      open={isExpanded}
                      onOpenChange={() => toggleCategory(group.category)}
                    >
                      <div className="flex items-center border-b border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800">
                        <div
                          className="flex items-center gap-2 p-2 pl-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCategory(
                              group.category,
                              group.collections,
                              selectionState !== 'all'
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectionState === 'all'}
                            className={selectionState === 'partial' ? 'opacity-50' : ''}
                            onCheckedChange={(checked) =>
                              handleSelectCategory(group.category, group.collections, checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <CollapsibleTrigger className="flex-1">
                          <div className="flex items-center justify-between p-2 pr-3 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-stone-500 dark:text-stone-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-stone-500 dark:text-stone-400" />
                              )}
                              <Badge variant="outline" className={group.config.color}>
                                {group.category}
                              </Badge>
                              <span className="text-xs text-stone-500 dark:text-stone-400">
                                {group.collections.length} collection{group.collections.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400">
                                    <Info className="h-3 w-3" />
                                    <span>{categoryDocs.toLocaleString()} docs</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  <p className="max-w-xs">{group.config.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <div className="divide-y divide-stone-100 dark:divide-stone-700">
                          {group.collections.map((collection) => {
                            const meta = getCollectionMeta(collection.name);
                            const isSelected = selectedCollections.includes(collection.name);
                            return (
                              <div
                                key={collection.name}
                                className={`flex items-start gap-3 p-3 pl-10 cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                                    : 'hover:bg-stone-50 dark:hover:bg-stone-800'
                                }`}
                                onClick={() => handleToggleCollection(collection.name)}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleToggleCollection(collection.name)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100 truncate">
                                      {collection.name}
                                    </span>
                                    {isSelected && (
                                      <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    )}
                                  </div>
                                  <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                    {meta.description}
                                  </p>
                                  <p className="text-xs text-stone-400 dark:text-stone-500 mt-1">
                                    {(collection.count || 0).toLocaleString()} documents
                                    {' · '}
                                    {formatBytes(collection.size_bytes)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })
              )}
            </div>

            {/* Selection Summary */}
            <div className="flex items-center justify-between text-sm">
              <p className="text-stone-600 dark:text-stone-300">
                {selectedCollections.length === 0 ? (
                  'No collections selected'
                ) : (
                  <>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{selectedCollections.length}</span>
                    {' '}collection{selectedCollections.length !== 1 ? 's' : ''} selected
                  </>
                )}
              </p>
              {selectedCollections.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCollections([])}
                  className="text-stone-500 hover:text-stone-700"
                >
                  Clear Selection
                </Button>
              )}
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <Label htmlFor="confirm-text" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Type <code className="bg-stone-100 dark:bg-stone-700 px-1.5 py-0.5 rounded text-red-600 dark:text-red-400">RESTORE</code>{' '}
              to confirm
            </Label>
            <Input
              id="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESTORE to confirm"
              className="font-mono"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-gradient-to-br from-red-800 to-red-900 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50"
          >
            <Database className="h-4 w-4 mr-2" />
            Restore {selectedCollections.length > 0 && `(${selectedCollections.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RestoreBackupModal;
